import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { AdminTabs } from './VoiceAdmin';

type EmailLog = {
  id: string;
  provider?: string;
  status?: string;
  success?: boolean;
  messageId?: string | null;
  toEmail?: string;
  toName?: string;
  subject?: string;
  senderEmail?: string;
  senderName?: string;
  sequenceId?: string | null;
  campaignId?: string | null;
  product?: string | null;
  userId?: string | null;
  plan?: string | null;
  tags?: string[];
  isTest?: boolean;
  adminEmail?: string | null;
  sentAt?: Timestamp | Date | number | null;
  deliveredAt?: Timestamp | Date | number | null;
  openedAt?: Timestamp | Date | number | null;
  clickedAt?: Timestamp | Date | number | null;
  failedAt?: Timestamp | Date | number | null;
  lastEvent?: string;
  lastEventAt?: Timestamp | Date | number | null;
  openCount?: number;
  clickCount?: number;
  clickedLink?: string | null;
  error?: string | null;
  lastError?: string | null;
  updatedAt?: Timestamp | Date | number | null;
  createdAt?: Timestamp | Date | number | null;
};

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const date = new Date(value < 1_000_000_000_000 ? value * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (
    typeof value === 'object'
    && value !== null
    && 'toDate' in value
    && typeof (value as Timestamp).toDate === 'function'
  ) {
    return (value as Timestamp).toDate();
  }
  return null;
};

const formatTimestamp = (...values: unknown[]) => {
  for (const value of values) {
    const date = toDate(value);
    if (date) return date.toLocaleString();
  }
  return 'N/A';
};

const normalizeStatus = (status?: string) => (status || 'recorded').replace(/[_-]+/g, ' ');

const statusTone = (status?: string, success?: boolean) => {
  const normalized = (status || '').toLowerCase();
  if (success === false || ['failed', 'hard_bounce', 'soft_bounce', 'blocked', 'spam', 'unsubscribe'].includes(normalized)) {
    return 'bg-red-50 border-red-200 text-red-700';
  }
  if (['opened', 'clicked'].includes(normalized)) {
    return 'bg-yellow-50 border-yellow-200 text-yellow-800';
  }
  if (success === true || ['sent', 'delivered'].includes(normalized)) {
    return 'bg-green-50 border-green-200 text-green-700';
  }
  if (normalized === 'deferred') return 'bg-amber-50 border-amber-200 text-amber-700';
  return 'bg-neutral-100 border-neutral-200 text-neutral-600';
};

const primaryTimestamp = (log: EmailLog) =>
  formatTimestamp(log.lastEventAt, log.updatedAt, log.sentAt, log.createdAt);

export const EmailLogsAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLogs = async () => {
    if (!db) {
      setLoading(false);
      setError('Firestore is not configured.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getDocs(query(collection(db, 'email-logs'), orderBy('updatedAt', 'desc'), limit(200)));
      const next = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...(docSnapshot.data() as Omit<EmailLog, 'id'>),
      }));
      setLogs(next);
      setSelectedLog((current) => {
        if (!current) return next[0] || null;
        return next.find((log) => log.id === current.id) || next[0] || null;
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to load email logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return logs;
    return logs.filter((log) =>
      [
        log.toEmail,
        log.toName,
        log.subject,
        log.status,
        log.sequenceId,
        log.campaignId,
        log.product,
        log.userId,
        log.messageId,
        log.adminEmail,
        ...(log.tags || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    );
  }, [logs, searchTerm]);

  const stats = useMemo(() => {
    const sent = logs.filter((log) => log.success === true && log.status !== 'failed').length;
    const failed = logs.filter((log) => log.success === false || log.status === 'failed').length;
    const opened = logs.filter((log) => log.openedAt || (log.openCount || 0) > 0 || log.status === 'opened' || log.status === 'clicked').length;
    const clicked = logs.filter((log) => log.clickedAt || (log.clickCount || 0) > 0 || log.status === 'clicked').length;
    return { total: logs.length, sent, failed, opened, clicked };
  }, [logs]);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Email Logs</div>
            <div className="text-xs text-neutral-500">
              Brevo sends, sequence metadata, and delivery status records.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button onClick={signOutUser} className="text-xs text-neutral-600 underline hover:text-neutral-900">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <AdminTabs current="emailLogs" />
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Logs" value={stats.total.toLocaleString()} />
          <StatCard label="Sent" value={stats.sent.toLocaleString()} />
          <StatCard label="Opened" value={stats.opened.toLocaleString()} />
          <StatCard label="Clicked" value={stats.clicked.toLocaleString()} />
          <StatCard label="Failed" value={stats.failed.toLocaleString()} />
        </div>

        <div className="mb-6 bg-white border border-neutral-200 rounded-xl px-4 py-3">
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by email, subject, sequence, campaign, user ID, or message ID"
            className="w-full text-sm outline-none placeholder:text-neutral-400"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Recent Emails</div>
                <div className="text-xs text-neutral-500 mt-1">Newest 200 entries from email-logs.</div>
              </div>
              <button
                onClick={fetchLogs}
                className="rounded-lg bg-neutral-900 px-4 py-2 text-xs font-semibold text-white hover:bg-neutral-800"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="p-8 text-sm text-neutral-500">Loading email logs...</div>
            ) : filteredLogs.length === 0 ? (
              <div className="p-8 text-sm text-neutral-500">No email logs found yet.</div>
            ) : (
              <div className="divide-y divide-neutral-100">
                {filteredLogs.map((log) => (
                  <button
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className={`w-full text-left px-6 py-4 transition hover:bg-neutral-50 ${
                      selectedLog?.id === log.id ? 'bg-yellow-50/70' : 'bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="font-semibold text-neutral-900 truncate">{log.subject || 'No subject recorded'}</div>
                        <div className="text-sm text-neutral-600 mt-1 truncate">{log.toEmail || 'No recipient recorded'}</div>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                          {log.sequenceId ? <span className="font-mono">{log.sequenceId}</span> : null}
                          {log.isTest ? <span>Test send</span> : null}
                          <span>{primaryTimestamp(log)}</span>
                        </div>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(log.status, log.success)}`}>
                        {normalizeStatus(log.status)}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <aside className="bg-white border border-neutral-200 rounded-xl p-6 h-fit">
            <div className="text-sm font-semibold text-neutral-900 mb-4">Log Details</div>
            {selectedLog ? (
              <div className="space-y-5">
                <Detail label="Subject" value={selectedLog.subject || 'N/A'} />
                <Detail label="Recipient" value={selectedLog.toEmail || 'N/A'} />
                <Detail label="Name" value={selectedLog.toName || 'N/A'} />
                <Detail label="Status" value={normalizeStatus(selectedLog.status)} />
                <Detail label="Provider" value={selectedLog.provider || 'brevo'} />
                <Detail label="Sequence" value={selectedLog.sequenceId || 'N/A'} />
                <Detail label="Campaign" value={selectedLog.campaignId || 'N/A'} />
                <Detail label="Product" value={selectedLog.product || 'N/A'} />
                <Detail label="User ID" value={selectedLog.userId || 'N/A'} />
                <Detail label="Plan" value={selectedLog.plan || 'N/A'} />
                <Detail label="Admin" value={selectedLog.adminEmail || 'N/A'} />
                <Detail label="Message ID" value={selectedLog.messageId || 'N/A'} />
                <Detail label="Sent" value={formatTimestamp(selectedLog.sentAt)} />
                <Detail label="Opened" value={`${formatTimestamp(selectedLog.openedAt)}${selectedLog.openCount ? ` (${selectedLog.openCount})` : ''}`} />
                <Detail label="Clicked" value={`${formatTimestamp(selectedLog.clickedAt)}${selectedLog.clickCount ? ` (${selectedLog.clickCount})` : ''}`} />
                <Detail label="Last event" value={selectedLog.lastEvent ? `${selectedLog.lastEvent} at ${formatTimestamp(selectedLog.lastEventAt)}` : 'N/A'} />

                {selectedLog.clickedLink ? (
                  <Detail label="Clicked link" value={selectedLog.clickedLink} />
                ) : null}

                {selectedLog.error || selectedLog.lastError ? (
                  <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-700">
                    {selectedLog.error || selectedLog.lastError}
                  </div>
                ) : null}

                <JsonBlock label="Tags" value={selectedLog.tags || []} />
              </div>
            ) : (
              <div className="text-sm text-neutral-500">Select an email log to inspect it.</div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-neutral-200 rounded-xl px-4 py-3">
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold">{label}</div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

const Detail: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-1">{label}</div>
    <div className="text-sm text-neutral-700 break-all">{value}</div>
  </div>
);

const JsonBlock: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2">{label}</div>
    <pre className="max-h-56 overflow-auto rounded-lg bg-neutral-950 p-3 text-xs text-neutral-100 whitespace-pre-wrap break-words">
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);
